/**
 * useCurrentQuarter
 *
 * Fetches the effective current quarter from the backend, which respects
 * equip_config_snapshot cutoff dates. Cached for 1 hour via staleTime.
 *
 * This is the authoritative source for "what quarter is current" in the
 * frontend. Do not use the calendar-based getCurrentQuarter() from
 * equip-utils.ts for routing decisions.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'

export const currentQuarterKeys = {
  current: () => ['equip', 'current-quarter'] as const,
}

export function useCurrentQuarter(enabled: boolean = true) {
  const query = useQuery({
    queryKey: currentQuarterKeys.current(),
    queryFn: () => equipApi.dashboard.getCurrentQuarter(),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 60 * 60 * 1000,
    enabled,
  })

  return {
    year: query.data?.year ?? 0,
    quarter: query.data?.quarter ?? 0,
    // A disabled query stays isPending forever (no fetch attempted), so gate on
    // isFetching too — otherwise callers that render null while loading (e.g.
    // EquipScopeProvider) would blank out forever for users without EQuIP access.
    isLoading: query.isPending && query.isFetching,
    isError: query.isError,
  }
}
